import React from 'react';
import { Box, Typography } from '@mui/material';

export interface DeltaTextProps {
  /** What this figure is now. */
  value: number | null | undefined;
  /** What it is being compared against. */
  against: number | null | undefined;
  /**
   * For a cost or a wait, where a fall is the good news. Defaults to false,
   * where up is good.
   */
  lowerIsBetter?: boolean;
  /** Names what the comparison is, for the tooltip a caller may wrap it in. */
  label?: string;
}

/**
 * The change between two figures, as a share of the one compared against:
 * "(+12.4%)" in green, "(−6.0%)" in red.
 *
 * Sits beside a number rather than in its own column, so a reader sees the
 * figure and how it moved as one thing. Renders nothing when either side is
 * missing, or when the comparison would divide by zero — a change against
 * nothing is not a change.
 */
export function DeltaText({ value, against, lowerIsBetter = false, label }: DeltaTextProps) {
  if (value === null || value === undefined || against === null || against === undefined || against === 0) {
    return null;
  }
  const pct = ((value - against) / Math.abs(against)) * 100;
  // Rounds to the same place it is shown, so a "+0.0%" is never coloured as
  // if it moved.
  const shown = Math.round(pct * 10) / 10;
  const good = shown === 0 ? null : lowerIsBetter ? shown < 0 : shown > 0;

  return (
    <Box
      component="span"
      aria-label={label ? `${label}: ${shown > 0 ? 'up' : 'down'} ${Math.abs(shown)}%` : undefined}
      sx={(t) => ({
        ...t.typography.caption,
        ml: 0.5,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        color:
          good === null
            ? t.tokens.text.tertiary
            : good
              ? t.tokens.semantic.success
              : t.tokens.semantic.danger,
      })}
    >
      ({shown > 0 ? '+' : shown < 0 ? '−' : ''}
      {Math.abs(shown).toFixed(1)}%)
    </Box>
  );
}

/** A figure with its change beside it, right-aligned for a numeric column. */
export function ValueWithDelta({
  children, value, against, lowerIsBetter, label,
}: DeltaTextProps & { children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', justifyContent: 'flex-end' }}>
      <Typography variant="body2" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {children}
      </Typography>
      <DeltaText value={value} against={against} lowerIsBetter={lowerIsBetter} label={label} />
    </Box>
  );
}
